import {expandSelection} from "../helpers/expand_selection";
import {rangeObj, decode} from "../helpers/column_names";
import {_changeCondition} from "./conditional_formats";
import {addStyle, setStyle} from "./styles";
import {adjustRow} from "./column_operations";
import { ignoreReset, ignoreUndo } from "./undo";

const borderKeys = ["border-top", "border-right", "border-bottom", "border-left"];

export function init(view){
	view.attachEvent("onCommand", (cm, config) => {
		if (cm.id === "sort-asc" || cm.id === "sort-desc"){
			sortRange(view, null, cm.id.replace("sort-",""));
		} else if (cm.id === "sort-multi") {
			sortRange(view, null, config);
		} else if (cm.id === "sort-sheet-asc" || cm.id === "sort-sheet-desc") {
			sortSheet(view, cm.id.replace("sort-sheet-", ""));
		}
	});

	const grid = view.$$("cells");
	view.attachEvent("onUndo", (action, _row, _column, data) => {
		if (action == "sort"){
			const state = grid.getScrollState();
			const sel = grid.getSelectArea();
			// data loading resets undo history, and
			// we need to preserve it
			ignoreReset(() => {
				view.$handleSelection = null;
				view.parse(data);

				grid.addSelectArea(sel.start, sel.end);
				grid.scrollTo(state.x, state.y);
			});
		}
	});
}

// config is optional for sortRange()
function normalizeConfig(config, range) {
	if (!config || typeof config === "string") {
		return {
			hasHeaders: false,
			levels: [{ column: range.start.column, direction: config || "asc" }]
		};
	}

	return {
		hasHeaders: !!config.hasHeaders,
		levels: config.levels.map(l => ({
			column: typeof l.column === "string" ? decode[l.column] : l.column,
			direction: l.direction || "asc"
		}))
	};
}

function denySort(view, config){
	return view.alert({
		text: config.text, type: "alert-error"
	});
}

export function sortRange(view, range, config){
	// if it's multiselect - no-op
	if (!range && Object.keys(view._table.getAllSelectAreas()).length > 1)
		return denySort(view, { text: webix.i18n.spreadsheet.labels["error-sort-multiselect"] });

	range = range || view._table.getSelectArea();
	if (!range) return;

	range = rangeObj(range, view);
	let fullrange = range;
	if (range.start.row === range.end.row)
		fullrange = expandSelection(range, view);

	const normalized = normalizeConfig(config, fullrange);
	const startRow = normalized.hasHeaders ? fullrange.start.row + 1 : fullrange.start.row;

	// no-op for empty or single-row range
	if (startRow >= fullrange.end.row) return;

	// block sort if merged cells are present
	if (hasInvalidSpans(view, startRow, fullrange.start.column, fullrange.end.row, fullrange.end.column)) {
		denySort(view, { text: webix.i18n.spreadsheet.labels["sort-merge-error"] });
		return;
	}

	// collect radio groups and block sort if any partially overlap the range
	const radios = collectRadios(view, startRow, fullrange.start.column, fullrange.end.row, fullrange.end.column);
	if (radios === false) {
		denySort(view, { text: webix.i18n.spreadsheet.labels["sort-radio-error"] });
		return;
	}

	sort(view, fullrange, startRow, normalized.levels, radios);
}

function sortSheet(view, dir) {
	const [rows, cols] = view._mPage.getSize();
	if (!rows || !cols) return;

	const selected = view.getSelectedId();
	const column = selected ? selected.column : 1;

	const range = {
		start: { row: 1, column: 1 },
		end: { row: rows, column: cols }
	};

	sortRange(view, range, {
		hasHeaders: false,
		levels: [{ column, direction: dir }]
	});
}

function hasInvalidSpans(view, startRow, startCol, endRow, endCol) {
	const pull = view.$$("cells").getSpan();
	if (!pull) return false;

	const totalRows = endRow - startRow + 1;
	let rowsWithSpans = 0;

	for (let r = startRow; r <= endRow; r++) {
		if (!pull[r]) continue;
		for (const c in pull[r]) {
			const col = c * 1;
			const [width, height] = pull[r][c];
			const spanEnd = col + width - 1;

			// skip spans fully outside the sort range
			if (spanEnd < startCol || col > endCol) continue;

			// span must cover exactly the full row width of the sort range
			if (col !== startCol || spanEnd !== endCol) return true;

			// multi-row spans can't be sorted as individual rows
			if (height > 1) return true;

			rowsWithSpans++;
		}
	}

	// if spans exist, every row must have one (range includes only spans)
	if (rowsWithSpans > 0 && rowsWithSpans < totalRows)
		return true;

	return false;
}

function captureMetadata(view, startRow, endRow, startCol, endCol) {
	const grid = view._table;
	const rows = {};
	const borders = {};
	const heights = {};
	for (let row = startRow; row <= endRow; row++) {
		heights[row] = grid.getItem(row).$height || grid.config.rowHeight;
		const cells = {};
		for (let col = startCol; col <= endCol; col++) {
			const style = view.getStyle(row, col);
			const conditions = view.conditions.get(row, col);
			const locked = view.isCellLocked(row, col);
			const comment = view.comments._pull[row] && view.comments._pull[row][col];
			const validation = view.validation._pull[row] && view.validation._pull[row][col];
			const editor = view.getCellEditor(row, col);

			cells[col] = { style, conditions, locked, comment, validation, editor };

			// save borders keyed by position (they stay in place, not move with data)
			if (style) {
				const cellBorders = {};
				let hasBorder = false;
				for (const key of borderKeys) {
					if (style.props[key]) {
						cellBorders[key] = style.props[key];
						hasBorder = true;
					}
				}
				if (hasBorder) {
					if (!borders[row]) borders[row] = {};
					borders[row][col] = cellBorders;
				}
			}

			// clear metadata before muon sort
			if (conditions)
				_changeCondition("remove", row, col, conditions, null, view);
			if (locked)
				view.lockCell(row, col, false);
			if (comment)
				view.comments.remove(row, col);
			if (validation)
				view.validation.remove(row, col);
			if (editor)
				view.setCellEditor(row, col, null);
		}
		rows[row] = cells;
	}
	return { rows, borders, heights };
}

const radioRe = /^=radio\((.+?)\)/i;

function parseRadio(math) {
	const m = math && typeof math === "string" && math.match(radioRe);
	if (!m) return null;
	const parts = m[1].split(",").map(v => v.trim());
	return {
		rowPos: +parts[0],
		colPos: +parts[1],
		rowCount: +parts[2],
		colCount: +parts[3],
		state: parts[4] || "0"
	};
}

// returns collected radio cells grouped by anchor, or false if any group is unsafe
// safe groups: horizontal (rowCount=1) - skipped, no fixup needed 
// multi-row spanning all sort rows (1-column or 2D) - collected for rowPos fixup
// blocked: partial range overlap, or multi-row group that doesn't cover the full sort height
function collectRadios(view, startRow, startCol, endRow, endCol) {
	const sortRowCount = endRow - startRow + 1;
	const groups = {};

	for (let r = startRow; r <= endRow; r++) {
		for (let c = startCol; c <= endCol; c++) {
			const radio = parseRadio(view.getCellValue(r, c, true));
			if (!radio) continue;

			const groupStartRow = r - radio.rowPos + 1;
			const groupEndRow = groupStartRow + radio.rowCount - 1;
			const groupStartCol = c - radio.colPos + 1;
			const groupEndCol = groupStartCol + radio.colCount - 1;

			// block: partial overlap with sort range
			if (groupStartRow < startRow || groupEndRow > endRow ||
				groupStartCol < startCol || groupEndCol > endCol)
				return false;

			// horizontal groups (rowCount=1): safe, move as a unit with their row, no fixup needed
			if (radio.rowCount === 1) continue;

			// multi-row groups must span the full sort range height to stay intact
			if (radio.rowCount !== sortRowCount)
				return false;

			// collect for rowPos fixup - key by group anchor to avoid same-shape collisions
			const key = `${groupStartRow},${groupStartCol}`;
			if (!groups[key]) groups[key] = [];
			groups[key].push({ row: r, col: c, radio });
		}
	}
	return groups;
}

function fixRadioGroups(view, radios, rowMap) {
	for (const key in radios) {
		const cells = radios[key];

		// unique source rows sorted by new target row position
		const rows = [...new Set(cells.map(cell => cell.row))];
		rows.sort((a, b) => rowMap[a] - rowMap[b]);

		// new rowPos for each source row (1-based, in sorted order)
		const newRowPos = {};
		rows.forEach((sourceRow, i) => { newRowPos[sourceRow] = i + 1; });

		// update all cells where rowPos changed (handles both 1-column and 2D groups)
		for (const { row, col, radio } of cells) {
			const updatedRowPos = newRowPos[row];
			if (updatedRowPos !== radio.rowPos) {
				view.setCellValue(rowMap[row], col,
					`=radio(${updatedRowPos}, ${radio.colPos}, ${radio.rowCount}, ${radio.colCount}, ${radio.state})`
				);
			}
		}
	}
}

function muonSort(view, startRow, start, end, levels) {
	const muonLevels = levels.map(l => ({
		index: l.column - start.column,
		dir: l.direction
	}));
	const from = view._mPage.cellID(startRow - 1, start.column - 1);
	const to = view._mPage.cellID(end.row - 1, end.column - 1);
	return view._mPage.sort(from, to, muonLevels);
}

function buildRowMap(permutation, startRow) {
	const rowMap = {};
	for (let i = 0; i < permutation.length; i++)
		rowMap[permutation[i] + 1] = startRow + i; // muon 0-based → spreadsheet 1-based, old row index -> new row index
	return rowMap;
}

function applyMetadata(view, metadata, rowMap, startCol, endCol) {
	for (const sourceRow in rowMap) {
		const targetRow = rowMap[sourceRow];
		for (let col = startCol; col <= endCol; col++) {
			const source = metadata[sourceRow][col];

			// style (without borders - borders are restored separately)
			const movedStyle = source.style;
			if (movedStyle) {
				let hadBorder = false;
				for (const key of borderKeys) {
					if (movedStyle.props[key]) {
						hadBorder = true;
						break;
					}
				}
				if (hadBorder) {
					const stripped = addStyle(view, borderKeys.reduce((acc, k) => { acc[k] = ""; return acc; }, {}), movedStyle);
					setStyle(view, { row: targetRow, column: col }, stripped);
				} else {
					setStyle(view, { row: targetRow, column: col }, movedStyle);
				}
			} else {
				setStyle(view, { row: targetRow, column: col }, null);
			}

			if (source.conditions)
				_changeCondition("update", targetRow, col, null, source.conditions, view);
			if (source.locked)
				view.lockCell(targetRow, col, true);
			if (source.comment)
				view.comments.add(targetRow, col, source.comment);
			if (source.validation)
				view.validation.add(targetRow, col, source.validation);
			if (source.editor)
				view.setCellEditor(targetRow, col, source.editor);
		}
	}
}

function restoreBorders(view, borders) {
	for (const row in borders) {
		for (const col in borders[row]) {
			const currentStyle = view.getStyle(row * 1, col * 1);
			const withBorders = addStyle(view, borders[row][col], currentStyle);
			setStyle(view, { row: row * 1, column: col * 1 }, withBorders);
		}
	}
}

// rows without wrap keep their original heights (moved correctly), rows with wrap get recalculated fresh
function adjustWrappedRows(view, rowMap, heights, startCol, endCol) {
	const grid = view._table;
	
	for (const sourceRow in rowMap) {
		const targetRow = rowMap[sourceRow];
		// move row heights with data
		grid.setRowHeight(targetRow, heights[sourceRow]);

		// recalculate rows that have wrap at their new position
		for (let col = startCol; col <= endCol; col++) {
			const style = view.getStyle(targetRow, col);
			if (style && style.props.wrap === "wrap") {
				adjustRow(targetRow, view);
				break;
			}
		}
	}
}

function sort(view, range, startRow, levels, radios) {
	const oldData = view.serialize();

	ignoreUndo(() => {
		const { start, end } = range;
		const { rows: metadata, borders, heights } = captureMetadata(view, startRow, end.row, start.column, end.column);
		const permutation = muonSort(view, startRow, start, end, levels);
		view.callEvent("onMathRefresh", []); // trigger list of cells outside of sort range - formulas that refer to cells in sort range need value update - done by muon sort
		const rowMap = buildRowMap(permutation, startRow);
		applyMetadata(view, metadata, rowMap, start.column, end.column);
		restoreBorders(view, borders);
		fixRadioGroups(view, radios, rowMap);
		adjustWrappedRows(view, rowMap, heights, start.column, end.column);
	}, view);

	view.callEvent("onAction", ["sort", {
		value: oldData,
		newValue: view.serialize()
	}]);

	view.refresh();
}
